<template>
    <v-expansion-panels
        v-model="isOpenState"
        class="rtls-expansion-panels attribute-filter-panel"
        tile
    >
        <v-expansion-panel focusable class="rtls-expansion-panel mb-4">
            <v-expansion-panel-header class="panel-header">
                <div class="body-2">
                    <span class="filter-text-title">
                        {{ $tkey('title') }}
                    </span>
                    <v-icon v-once class="filter-text">
                        filter_list
                    </v-icon>
                    <span class="font-weight-bold filter-text">
                        {{ $tkey('numberOfProducts') }}
                        <template v-if="!getProductsInProgress">
                            {{ productsNumberInPromotion }}
                        </template>
                        <template v-else>
                            <spinner-dynamic class="pt-1" />
                        </template>
                    </span>
                    <div
                        v-if="showOverwlowError"
                        class="attribute-filter-panel__max-products-error"
                    >
                        <span class="error--text">
                            {{ $tkey('maxProductsNumberError', { maxAllowedProductCount }) }}
                        </span>
                    </div>
                </div>
                <template v-slot:actions>
                    <v-icon v-once color="indigo darken-2">
                        expand_more
                    </v-icon>
                </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="panel-header">
                <v-card-text class="px-6 pt-3">
                    <v-flex
                        v-for="attributeRow in activeAttributesCount"
                        :key="attributeScope[attributeRow - 1].key"
                    >
                        <attribute-filter
                            :key="`Filter::${attributeScope[attributeRow - 1].key}`"
                            :attribute-row-index="attributeRow - 1"
                            :attribute-scope="stagingAreaPromotion.attributeScope"
                            :validate-attribute-select="activeAttributesCount > 1"
                            @remove-attribute-row="onRemoveAttributeRow"
                            @products-number-updated="debouncedOnProductsNumberUpdated"
                        />
                        <v-divider :key="`Divider::${attributeRow}`" class="my-2" />
                    </v-flex>

                    <!-- Button to add an additional filter rule -->
                    <div class="attribute-filter-panel__actions">
                        <v-btn
                            :disabled="addButtonDisabled"
                            right
                            icon
                            text
                            depressed
                            @click="addAttributeRow"
                        >
                            <v-icon v-once color="indigo darken-2">add_box</v-icon>
                        </v-btn>
                        <!--There is desicion to not use these buttons-->
                        <!--If decision would change uncomment below code-->

                        <div class="attribute-filter-panel__actions--confirmation">
                            <!-- <confirm-dialog
                                class="confirm-dialog"
                                :question-text="$tkey('revertQuestionText')"
                                :warning-text="$tkey('revertWarningText')"
                                :action-text="$tkey('revertActionText')"
                                @confirm="revertAttributeScope"
                            >
                                <template v-slot:actions="{ activator }">
                                    <v-btn
                                        :disabled="isAttributeScopeUnchanged || saveInProgress"
                                        text
                                        primary
                                        small
                                        v-on="activator"
                                    >
                                        {{ $tkey('revertActionText') }}
                                    </v-btn>
                                </template>
                            </confirm-dialog>
                            <confirm-dialog
                                v-if="!isSavingInProgress"
                                ref="saveDialog"
                                class="confirm-dialog"
                                :question-text="$tkey('saveQuestionText')"
                                :warning-text="
                                    $tkey('saveWarningText', { productsNumberInPromotion })
                                "
                                :action-text="$tkey('saveActionText')"
                                @confirm="saveProductsToAttributeScopePromotion"
                            >
                                <template v-slot:actions>
                                    <positive-action-button
                                        v-if="!isSavingInProgress"
                                        :disabled="saveDialogDisabled"
                                        @onClick="openSaveDialog"
                                    >
                                        {{ $tkey('saveActionText') }}
                                    </positive-action-button>
                                </template>
                            </confirm-dialog> -->
                            <spinner-dynamic v-if="isSavingInProgress" class="pt-2" />
                        </div>
                    </div>
                </v-card-text>
            </v-expansion-panel-content>
        </v-expansion-panel>
    </v-expansion-panels>
</template>

<script>
import { mapGetters, mapActions, mapState, mapMutations } from 'vuex';
import { cloneDeep, debounce } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { offer } from '@enums/promotion-tabs';

const emptyAttribute = {
    attributeKey: null,
    attributeName: null,
    values: [],
};
export default {
    localizationKey: 'planning.promotionsViewer.attributeScopePromotion',
    props: {
        stagingAreaPromotion: {
            type: Object,
            required: true,
        },
        namespace: {
            type: String,
            required: true,
        },
        productOfferGroupId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            productsNumberInPromotion: 0,
            cacheAttributeScope: null,
            userInteraction: false,
            isSavingInProgress: false,
            isOpenState: 0,
            getProductsInProgress: false,
            getProductsRequestId: null,
        };
    },
    events: {
        async onForecastAndSaveAttributeScopedPromotion() {
            await this.saveProductsToAttributeScopePromotion();
        },
    },
    computed: {
        ...mapState('clientConfig', ['generalConfig']),
        ...mapState('promotions', ['tabValidationState', 'saveInProgress']),
        ...mapGetters('attributeMetadata', ['getAvailableAttributes']),
        activeAttributesCount() {
            return this.stagingAreaPromotion.attributeScope.length;
        },
        attributeScope() {
            return this.stagingAreaPromotion.attributeScope || [];
        },
        numFiltersApplied() {
            return this.attributeScope.filter(attr => attr.attributeKey).length;
        },
        addButtonDisabled() {
            return (
                this.getAvailableAttributes.length === this.attributeScope.length ||
                this.attributeScope.length === this.maxAttributesNumber ||
                this.isReadOnly
            );
        },
        showOverwlowError() {
            return this.productsNumberInPromotion > this.maxAllowedProductCount;
        },
        saveDialogDisabled() {
            return !this.isValid || this.showOverwlowError || this.getProductsInProgress;
        },
        isValid() {
            if (!this.stagingAreaPromotion) {
                return false;
            }
            const tabsValid =
                this.tabValidationState.channels === 'completed' &&
                this.tabValidationState.date === 'completed';
            // global promotion conditions
            if ((!tabsValid && this.namespace === 'default') || this.saveInProgress) {
                return false;
            }
            // local state conditions
            if (
                this.stagingAreaPromotion.attributeScope.length === 1 &&
                this.stagingAreaPromotion.attributeScope[0].attributeKey === null
            ) {
                return true;
            }
            return this.stagingAreaPromotion.attributeScope.every(attr => {
                return attr.attributeKey && attr.values.length > 0;
            });
        },
        maxAttributesNumber() {
            return this.generalConfig.maxAttributesPerPromotion;
        },
        maxAllowedProductCount() {
            return this.generalConfig.maxProductsPerPromotion;
        },
        showRemoveAllProductsMessage() {
            return this.productsNumberInPromotion === 0 && this.userInteraction;
        },
        /**
         * Determines whether the attribute scope remains unchanged between two given scopes.
         * Compares the provided previous and current attribute scopes to check if they are equivalent,
         * indicating that no changes have occurred in the attribute scope context.
         *
         * @returns {boolean} Returns true if the attribute scopes are unchanged; otherwise, false.
         */
        isAttributeScopeUnchanged() {
            if (!this.cacheAttributeScope || !this.attributeScope) return false;
            return JSON.stringify(this.attributeScope) === JSON.stringify(this.cacheAttributeScope);
        },
    },
    watch: {
        saveDialogDisabled(newValue) {
            this.setAttributeFilterValidation({
                namespace: this.namespace,
                value: !newValue,
            });
        },
    },
    mounted() {
        if (!this.stagingAreaPromotion.attributeScope.length) {
            this.stagingAreaPromotion.attributeScope.push({ ...emptyAttribute, key: uuidv4() });
        }

        // This ensures that modifications to 'cacheAttributeScope' do not affect the original 'attributeScope' object.
        // cacheAttributeScope is used to revert changes if needed.
        this.cacheAttributeScope = cloneDeep(this.stagingAreaPromotion.attributeScope);

        if (!this.stagingAreaPromotion.products.length && this.numFiltersApplied) {
            this.debouncedOnProductsNumberUpdated();
        } else {
            this.productsNumberInPromotion = this.stagingAreaPromotion.products.length;
        }
    },
    methods: {
        ...mapActions('products', ['fetchProductsNumberInPromotion']),
        ...mapActions('promotions', ['saveAttributeScopePromotion']),
        ...mapMutations('promotions', ['setUnsavedPromotion', 'setAttributeFilterValidation']),
        addAttributeRow() {
            const newAttributes = [
                {
                    attributeKey: null,
                    attributeName: null,
                    values: [],
                    key: uuidv4(),
                },
            ];
            if (!this.attributeScope.length) {
                newAttributes.push({
                    attributeKey: null,
                    attributeName: null,
                    values: [],
                });
            }
            this.stagingAreaPromotion.attributeScope = [...this.attributeScope, ...newAttributes];
            this.userInteraction = true;
            this.setUnsavedPromotion({ namespace: this.namespace, tab: offer, value: true });
        },
        onRemoveAttributeRow(event) {
            const { index } = event;
            this.userInteraction = true;
            this.setUnsavedPromotion({ namespace: this.namespace, tab: offer, value: true });
            if (this.stagingAreaPromotion.attributeScope.length <= 1) {
                this.stagingAreaPromotion.attributeScope = [{ ...emptyAttribute, key: uuidv4() }];
                this.productsNumberInPromotion = 0;
                return;
            }

            this.stagingAreaPromotion.attributeScope.splice(index, 1);
            this.debouncedOnProductsNumberUpdated();
        },
        async onProductsNumberUpdated() {
            this.userInteraction = true;
            this.setUnsavedPromotion({ namespace: this.namespace, tab: offer, value: true });

            if (!this.numFiltersApplied) {
                this.productsNumberInPromotion = 0;
                return;
            }

            this.getProductsInProgress = true;
            this.getProductsRequestId = uuidv4();
            const result = await this.fetchProductsNumberInPromotion({
                categories: this.stagingAreaPromotion.userSelectedCategories,
                attributeScope: this.stagingAreaPromotion.attributeScope,
                uuid: this.getProductsRequestId,
            });
            if (result.uuid === this.getProductsRequestId) {
                this.productsNumberInPromotion = result.count;
            }
            this.getProductsInProgress = false;
        },
        debouncedOnProductsNumberUpdated: debounce(async function() {
            await this.onProductsNumberUpdated();
        }, 500),
        revertAttributeScope() {
            if (this.cacheAttributeScope) {
                this.stagingAreaPromotion.attributeScope = cloneDeep(this.cacheAttributeScope);
            } else {
                this.stagingAreaPromotion.attributeScope = [{ ...emptyAttribute, key: uuidv4() }];
            }
            this.productsNumberInPromotion = this.stagingAreaPromotion.products.length;
            this.userInteraction = false;
            this.setUnsavedPromotion({ namespace: this.namespace, tab: 'all', value: false });
        },
        async saveProductsToAttributeScopePromotion() {
            this.isSavingInProgress = true;
            try {
                await this.saveAttributeScopePromotion({
                    namespace: this.namespace,
                    attributeScope: this.stagingAreaPromotion.attributeScope,
                    categories: this.stagingAreaPromotion.userSelectedCategories,
                    productOfferGroupId: this.productOfferGroupId,
                });
                this.cacheAttributeScope = cloneDeep(this.stagingAreaPromotion.attributeScope);
                this.userInteraction = false;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error saving attribute scope promotion:', error);
            }
            this.isSavingInProgress = false;
        },
        openSaveDialog() {
            this.$refs.saveDialog.open();
        },
        resetAttributesScopeProducts() {
            if (this.numFiltersApplied) {
                this.debouncedOnProductsNumberUpdated();
                this.userInteraction = true;
                this.setUnsavedPromotion({ namespace: this.namespace, tab: offer, value: true });
            }
        },
    },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';

.attribute-filter-panel {
    &__max-products-error {
        padding-top: 0.3rem;

        .error--text {
            font-size: 1.2rem;
        }
    }

    .panel-header {
        background-color: $rtls-border-colour !important;
        min-height: 7rem !important;
        border-bottom: 0.1rem solid rgba(208, 208, 208, 0.87);

        &::v-deep .v-label,
        &::v-deep .v-chip__content {
            font-size: 1.2rem;
        }
    }

    .filter-text {
        color: $promo-text-colour-2;
        font-size: 1.25rem;
    }

    .filter-text-title {
        font-size: 1.25rem;
    }

    &__actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 60%;
        padding-top: 1rem;

        .v-btn--disabled {
            color: $promo-white !important;
        }
    }
}
</style>
